import { useCallback } from "react";
import { notifyIcon, quote } from "../../../../config";
import { getNotification } from "../../../../utils/common";
import { NODE_LABELS, STARTER_NODE_UI_ID } from "../ruleBasedFlow.constants";
import { createAddEdgePayload, createAddTaskEdgePayload, createAddNodePayload, createEdge, createStarterEdge, createAddTaskNodePayload } from "../ruleBasedFlow.helpers";
import * as ruleBasedFlowService from "../ruleBasedFlow.service";
import { deleteTaskNodeExpression, deleteWorkflowNodeExpression } from "../../ExpressionBuilder/services/expressionBuilder.service";

/**
 * Custom hook for node operations (add, update, delete)
 * @param {string} entityId - Workflow ID (workflow context) or Task ID (task context)
 * @param {string} context - "workflow" | "task"
 * @param {string} firstNodeId - ID of the first node in workflow
 * @param {Function} setFirstNodeId - Function to set first node ID
 * @param {Function} setNodes - Function to update nodes state
 * @param {Function} setEdges - Function to update edges state
 * @param {Function} handleAddNodeBetween - Callback for adding node between edges
 * @param {Function} deleteEdge - Function to delete edges
 */
export const useNodeOperations = (entityId, context, firstNodeId, setFirstNodeId, setNodes, setEdges, handleAddNodeBetween, deleteEdge) => {
  /**
   * Adds a new node to the workflow or task flow
   * @param {string} nodeType - Type of node to add
   * @param {Object} position - Node position {x, y}
   * @param {string} sourceNodeId - Source node ID for edge connection
   * @param {string} sourceHandle - Source handle identifier
   * @param {boolean} forceIsFirstNode - Optional: Force isFirstNode to true (for starter edge add node between)
   */
  const addNode = useCallback(
    async (nodeType, position, sourceNodeId = null, sourceHandle = null, forceIsFirstNode = false) => {
      if (!entityId) {
        return null;
      }

      const isFirstNode = forceIsFirstNode || !firstNodeId;
      const nodeLabel = NODE_LABELS[nodeType] || nodeType;

      let savedNode;
      try {
        if (context === "task") {
          const payload = createAddTaskNodePayload(entityId, nodeType, position, isFirstNode);
          savedNode = await ruleBasedFlowService.addTaskNode(payload);
        } else {
          const payload = createAddNodePayload(entityId, nodeType, position, isFirstNode);
          savedNode = await ruleBasedFlowService.addNode(payload);
        }

        if (!savedNode || !savedNode._id) {
          throw new Error("Failed to create node - no ID returned");
        }

        const newNode = {
          id: savedNode._id,
          type: nodeType,
          position: position,
          data: {
            label: nodeLabel,
            isFirstNode: isFirstNode,
          },
        };

        setNodes((nds) => [...nds, newNode]);

        return { savedNode, isFirstNode };
      } catch (error) {
        getNotification("Failed to add node. Please try again.", notifyIcon.ERROR_ICON);
        return null;
      }
    },
    [entityId, context, firstNodeId]
  );

  /**
   * Creates an edge in the backend
   * @param {string} sourceNodeId - Source node ID
   * @param {string} targetNodeId - Target node ID
   * @param {string} sourceHandle - Source handle identifier
   */
  const addEdgeToBackend = useCallback(
    async (sourceNodeId, targetNodeId, sourceHandle) => {
      if (!entityId) {
        getNotification(` ${context === "task" ? quote.INVALID_TASK : quote.INVALID_WORKFLOW}`, notifyIcon.ERROR_ICON);
        return null;
      }

      try {
        let edgeResponse;
        if (context === "task") {
          const edgePayload = createAddTaskEdgePayload(sourceNodeId, targetNodeId, entityId, sourceHandle);
          edgeResponse = await ruleBasedFlowService.addTaskNodeEdge(edgePayload);
        } else {
          const edgePayload = createAddEdgePayload(sourceNodeId, targetNodeId, entityId, sourceHandle);
          edgeResponse = await ruleBasedFlowService.addNodeEdge(edgePayload);
        }

        if (!edgeResponse) {
          getNotification("Failed to save connection. Please try again.", notifyIcon.ERROR_ICON);
          return null;
        }

        return edgeResponse;
      } catch (error) {
        getNotification("Failed to save connection. Please try again.", notifyIcon.ERROR_ICON);
        return null;
      }
    },
    [entityId, context]
  );

  /**
   * Creates UI-only edge from starter node to first node and sets firstNodeId.
   * Works for both workflow and task contexts (starter edge is always UI-only).
   * @param {string} nodeId - Target node ID
   */
  const handleFirstNodeEdge = useCallback(
    (nodeId) => {
      setFirstNodeId(nodeId);
      const starterEdgeBase = createStarterEdge(nodeId);
      const starterEdge = {
        ...starterEdgeBase,
        data: {
          onAddNode: handleAddNodeBetween,
          onDeleteEdge: deleteEdge,
          sourceHandle: starterEdgeBase.sourceHandle,
        },
      };
      setEdges((eds) => [...eds, starterEdge]);
    },
    [setFirstNodeId, setEdges, handleAddNodeBetween, deleteEdge]
  );

  /**
   * Creates edge between two regular nodes.
   * Uses task-specific endpoint in task context.
   * @param {string} sourceNodeId - Source node ID
   * @param {string} targetNodeId - Target node ID
   * @param {string} sourceHandle - Source handle identifier
   * @returns {string|null} MongoDB edge ID or null if failed
   */
  const handleRegularNodeEdge = useCallback(
    async (sourceNodeId, targetNodeId, sourceHandle) => {
      const tempEdge = {
        ...createEdge(sourceNodeId, targetNodeId, sourceHandle),
        data: {
          onAddNode: handleAddNodeBetween,
          onDeleteEdge: deleteEdge,
          sourceHandle: sourceHandle,
        },
      };
      const tempEdgeId = tempEdge.id;
      setEdges((eds) => [...eds, tempEdge]);

      const edgeResponse = await addEdgeToBackend(sourceNodeId, targetNodeId, sourceHandle);

      if (!edgeResponse || !edgeResponse._id) {
        setEdges((eds) => eds.filter((edge) => edge.id !== tempEdgeId));
        return null;
      }

      setEdges((eds) =>
        eds.map((edge) =>
          edge.id === tempEdgeId
            ? {
                ...edge,
                id: edgeResponse._id,
                data: {
                  ...edge.data,
                  onAddNode: handleAddNodeBetween,
                  onDeleteEdge: deleteEdge,
                  sourceHandle: edge.sourceHandle,
                },
              }
            : edge
        )
      );

      return edgeResponse._id;
    },
    []
  );

  /**
   * Deletes a node and its connected edges
   * Uses task-specific endpoint in task context, workflow endpoint otherwise
   * @param {string} nodeId - Node ID to delete
   */
  const deleteNode = useCallback(
    async (nodeId, nodeType) => {
      if (!entityId) {
        getNotification(`${context === "task" ? quote.INVALID_TASK : quote.INVALID_WORKFLOW}`, notifyIcon.ERROR_ICON);
        return false;
      }

      try {
        let deleteNodeResponse;
        if (context === "task") {
          deleteNodeResponse = await ruleBasedFlowService.deleteTaskNode({ nodeId, taskId: parseInt(entityId) });
        } else {
          deleteNodeResponse = await ruleBasedFlowService.deleteNode({ nodeId, workflowId: parseInt(entityId) });
          if (nodeType === "switchNode") {
            await deleteWorkflowNodeExpression({ id: nodeId });
          }
        }
        if (!deleteNodeResponse) {
          getNotification(quote.FAILED_TO_DELETE_NODE, notifyIcon.ERROR_ICON);
          return false;
        }

        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));

        getNotification(quote.NODE_DELETED_SUCCESSFULLY, notifyIcon.SUCCESS_ICON);
        return true;
      } catch (error) {
        getNotification(quote.FAILED_TO_DELETE_NODE, notifyIcon.ERROR_ICON);
        return false;
      }
    },
    [entityId, context]
  );

  /**
   * Removes the existing UI edge from starter to old first node
   * @param {string} oldFirstNodeId - The old first node ID
   */
  const removeStarterEdge = useCallback(
    (oldFirstNodeId) => {
      setEdges((eds) => eds.filter((edge) => !(edge.source === STARTER_NODE_UI_ID && edge.target === oldFirstNodeId)));
    },
    [setEdges]
  );

  /**
   * Creates new UI edge from starter to new node
   * @param {string} newNodeId - New node ID
   * @param {string} sourceHandle - Source handle identifier
   * @returns {Object} Edge object
   */
  const createStarterEdgeToNode = useCallback(
    (newNodeId, sourceHandle) => {
      const starterEdgeBase = createStarterEdge(newNodeId);
      const starterEdge = {
        ...starterEdgeBase,
        sourceHandle: sourceHandle || starterEdgeBase.sourceHandle,
        data: {
          onAddNode: handleAddNodeBetween,
          onDeleteEdge: deleteEdge,
          sourceHandle: sourceHandle || starterEdgeBase.sourceHandle,
        },
      };
      setEdges((eds) => [...eds, starterEdge]);
      return starterEdge;
    },
    []
  );

  /**
   * Creates backend edge from new node to old first node
   * @param {string} newNodeId - New node ID
   * @param {string} oldFirstNodeId - Old first node ID
   * @param {string} sourceHandle - Source handle identifier
   * @returns {string|null} Edge ID or null if failed
   */
  const createBackendEdgeFromNewToOld = useCallback(
    async (newNodeId, oldFirstNodeId, sourceHandle) => {
      const handle = sourceHandle || "bottom";
      
      // Create temp edge first (optimistic UI update)
      const tempEdge = {
        ...createEdge(newNodeId, oldFirstNodeId, handle),
        data: {
          onAddNode: handleAddNodeBetween,
          onDeleteEdge: deleteEdge,
          sourceHandle: handle,
        },
      };
      const tempEdgeId = tempEdge.id;
      setEdges((eds) => [...eds, tempEdge]);

      // Create edge in backend
      const edgeResponse = await addEdgeToBackend(newNodeId, oldFirstNodeId, handle);
      
      if (!edgeResponse || !edgeResponse._id) {
        // Rollback: remove temp edge if backend call failed
        setEdges((eds) => eds.filter((edge) => edge.id !== tempEdgeId));
        return null;
      }

      // Replace temp edge with real backend edge
      setEdges((eds) =>
        eds.map((edge) =>
          edge.id === tempEdgeId
            ? {
                ...edge,
                id: edgeResponse._id,
                data: {
                  ...edge.data,
                  onAddNode: handleAddNodeBetween,
                  onDeleteEdge: deleteEdge,
                  sourceHandle: edge.sourceHandle,
                },
              }
            : edge
        )
      );

      return edgeResponse._id;
    },
    []
  );

  /**
   * Updates old first node's isFirstNode flag to false
   * Uses task-specific endpoint in task context
   * @param {string} oldFirstNodeId - Old first node ID
   */
  const updateOldFirstNodeFlag = useCallback(
    async (oldFirstNodeId) => {
      if (!entityId) {
        return false;
      }

      try {
        let updateNodeResponse;
        if (context === "task") {
          updateNodeResponse = await ruleBasedFlowService.updateTaskNode({
            nodeId: oldFirstNodeId,
            taskId: parseInt(entityId),
            isFirstNode: false,
          });
        } else {
          updateNodeResponse = await ruleBasedFlowService.updateNode({
            nodeId: oldFirstNodeId,
            workflowId: parseInt(entityId),
            isFirstNode: false,
          });
        }

        if (!updateNodeResponse) {
          getNotification(quote.FAILED_TO_UPDATE_FIRST_NODE_STATUS, notifyIcon.ERROR_ICON);
          return false;
        }

        // Update UI to reflect isFirstNode: false
        setNodes((nds) =>
          nds.map((node) =>
            node.id === oldFirstNodeId
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    isFirstNode: false,
                  },
                }
              : node
          )
        );

        return true;
      } catch (error) {
        getNotification("Failed to update first node status", notifyIcon.ERROR_ICON);
        return false;
      }
    },
    [entityId, context,]
  );

  /**
   * Main handler for adding node between starter edge.
   * Fully supported in both workflow and task contexts.
   * @param {string} nodeType - Type of node to add
   * @param {Object} position - Node position {x, y}
   * @param {string} oldFirstNodeId - The current first node (target of UI edge)
   * @param {string} sourceHandle - Handle identifier (usually "bottom")
   * @returns {Object|null} { savedNode, success } or null if failed
   */
  const handleStarterEdgeAddNodeBetween = useCallback(
    async (nodeType, position, oldFirstNodeId, sourceHandle) => {
      if (!entityId) {
        getNotification("Invalid workflow", notifyIcon.ERROR_ICON);
        return null;
      }

      // Step 1: Add new node with isFirstNode: true (force it even though firstNodeId exists)
      const result = await addNode(nodeType, position, null, null, true);
      if (!result) {
        return null;
      }

      const { savedNode } = result;

      try {
        // Step 2: Remove existing UI edge (no backend call)
        removeStarterEdge(oldFirstNodeId);

        // Step 3: Create new UI edge from starter to new node (no backend call)
        createStarterEdgeToNode(savedNode._id, sourceHandle);

        // Step 4: Create backend edge from new node to old first node
        const edgeId = await createBackendEdgeFromNewToOld(savedNode._id, oldFirstNodeId, sourceHandle || "bottom");
        if (!edgeId) {
          // Rollback: restore UI edge if backend edge creation failed
          createStarterEdgeToNode(oldFirstNodeId, sourceHandle);
          getNotification("Failed to create connection. Please try again.", notifyIcon.ERROR_ICON);
          return null;
        }

        // Step 5: Update old first node's isFirstNode flag to false
        const updateSuccess = await updateOldFirstNodeFlag(oldFirstNodeId);
        if (!updateSuccess) {
          // Non-critical error, but log it
          console.warn("Failed to update old first node flag, but node was added successfully");
        }

        // Update firstNodeId state (already done by addNode when isFirstNode: true)
        setFirstNodeId(savedNode._id);

        return { savedNode, success: true };
      } catch (error) {
        getNotification("Failed to add node. Please try again.", notifyIcon.ERROR_ICON);
        return null;
      }
    },
    [context, entityId]
  );

  return {
    addNode,
    addEdgeToBackend,
    handleFirstNodeEdge,
    handleRegularNodeEdge,
    deleteNode,
    handleStarterEdgeAddNodeBetween,
  };
};
